import { AdDataSet } from './DataSet/Ad';
import { EntryPointDataSet } from './DataSet/EntryPoint';
import { EventDataSet } from './DataSet/Event';
import { MetaDataSet } from './DataSet/Meta';
import { MissionDataSet } from './DataSet/Mission';
import { AdDataHandler } from './Handler/Ad';
import { EntryPointDataHandler } from './Handler/EntryPoint';
import { EventDataHandler } from './Handler/Event';
import { MetaDataHandler } from './Handler/Meta';
import { MissionDataHandler } from './Handler/Mission';
import { IDataHandler } from './IDataHandler';
import { allTrackingEvents, TrackingEventName } from './TrackingEvent';
import { getTimeZone } from '../DateHelper';
import { BoosterType, PreBoosterType } from 'State/Card/BoosterType';
import { getCurrentLevelNumber, getLevelStarsTotal } from 'State/Player/PlayerLevelHelper';
import { ResourceDataHandler } from './Handler/Resource';
import { ResourceDataSet } from './DataSet/Resource';
import { TransactionDataSet } from './DataSet/Transaction';
import { TransactionDataHandler } from './Handler/Transaction';
import { FTUXDataHandler } from './Handler/FTUX';
import { SocialActionDataHandler } from './Handler/SocialAction';
import { SocialActionDataSet } from './DataSet/SocialAction';
import { FTUXDataSet } from './DataSet/FTUX';
import { RewardType } from '../Rewards/RewardType';
import { IGameState } from 'State/Game/IGameState';
import { report } from 'report';
import { cached } from '../async';
import { QuestDataHandler } from './Handler/Quest';
import { QuestDataSet } from './DataSet/Quest';
import { IPlayerStateQuestData } from 'State/Player/IPlayerState';
import QuestHelper from '../QuestHelper';
import { ProgressStateHelper } from 'State/Player/Progress/ProgressStateHelper';
import { LevelType } from 'State/Player/Progress/PlayerLevels';
import { Platform } from 'Utility/Platform';
import { UserDataHandler } from './Handler/User';
import { UserDataSet } from './DataSet/User';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import Game from 'Game';
import { DataRestoreDataHandler } from './Handler/DataRestore';
import { DataRestoreDataSet } from './DataSet/DataRestore';
import { ConnectionDataSet } from './DataSet/Connection';
import { ConnectionDataHandler } from './Handler/Connection';
import { GBCHandler } from 'Utility/SDK/GBCHandler';

export enum TrackMethod {
    SET,
    ADD,
    SUBTRACT,
    MAX,
    MIN,
    STREAK,
    STREAK_END,
    ONCE,
}

export interface IDataSet {}
export const STRING_DATA_NOT_FOUND = 'NotFound';

let dataHandlers: Array<IDataHandler<IDataSet>>;

let eventDataHandler: EventDataHandler<TrackingDataSet>;
let metaDataHandler: MetaDataHandler;
let missionDataHandler: MissionDataHandler;
let entryPointDataHandler: EntryPointDataHandler;
let adDataHandler: AdDataHandler;
let resourceDataHandler: ResourceDataHandler;
let transactionDataHandler: TransactionDataHandler;
let ftuxDataHandler: FTUXDataHandler;
let socialActionHandler: SocialActionDataHandler;
let questDataHandler: QuestDataHandler;
let userDataHandler: UserDataHandler;
let dataRestoreHandler: DataRestoreDataHandler;
let connectionDataHandler: ConnectionDataHandler;
let sessionEventCounter = 1;

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export type TrackingDataSet = Partial<
    Omit<EventDataSet<never>, 'eventParams'> &
        MetaDataSet &
        MissionDataSet &
        AdDataSet &
        EntryPointDataSet &
        ResourceDataSet &
        TransactionDataSet &
        SocialActionDataSet &
        FTUXDataSet &
        QuestDataSet &
        UserDataSet &
        DataRestoreDataSet &
        ConnectionDataSet
>;

let trackingEventDataMap: {
    [key in allTrackingEvents]: IDataHandler<IDataSet>[];
};

export const handleTrackTimeToFirstInteractionPossible = cached(() => {
    const key: keyof TrackingDataSet = 'timeUntilFirstUserInteractionPossible';

    // "calls a specified function to update an animation before the next repaint"
    // therefore, calling it twice should guarantee it executes once drawing/gpu has settled
    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
            const time: number = trackLoadingTime();
            console.log(`[DEBUG] ${key} = ${time}`);
            track(key, time, TrackMethod.ONCE);

            resetEventHandlerData(TrackingEventName.gameLoaded, Game.store.getState());
        });
    });
});

export function initializeTrackingDataHandlers() {
    metaDataHandler = new MetaDataHandler();
    missionDataHandler = new MissionDataHandler();
    socialActionHandler = new SocialActionDataHandler();
    eventDataHandler = new EventDataHandler<TrackingDataSet>();
    entryPointDataHandler = new EntryPointDataHandler();
    adDataHandler = new AdDataHandler();
    resourceDataHandler = new ResourceDataHandler();
    transactionDataHandler = new TransactionDataHandler();
    ftuxDataHandler = new FTUXDataHandler();
    questDataHandler = new QuestDataHandler();
    userDataHandler = new UserDataHandler();
    dataRestoreHandler = new DataRestoreDataHandler();
    connectionDataHandler = new ConnectionDataHandler();

    dataHandlers = [
        eventDataHandler,
        metaDataHandler,
        missionDataHandler,
        entryPointDataHandler,
        adDataHandler,
        resourceDataHandler,
        transactionDataHandler,
        socialActionHandler,
        ftuxDataHandler,
        questDataHandler,
        userDataHandler,
        dataRestoreHandler,
        connectionDataHandler,
    ];

    trackingEventDataMap = {
        missionStarted: [missionDataHandler],
        missionCompleted: [missionDataHandler],
        missionFailed: [missionDataHandler],
        newPlayer: [entryPointDataHandler],
        gameStarted: [entryPointDataHandler, connectionDataHandler],
        gameLoaded: [entryPointDataHandler, connectionDataHandler],
        adShow: [adDataHandler],
        adRequest: [adDataHandler],
        resource: [resourceDataHandler, transactionDataHandler, adDataHandler],
        transaction: [transactionDataHandler],
        ftux: [ftuxDataHandler],
        socialAction: [socialActionHandler],
        questStart: [questDataHandler],
        userUnification: [userDataHandler],
        myFarmUpdate: [transactionDataHandler],
        dataRestore: [dataRestoreHandler],
        gbFail: [connectionDataHandler],
        gbReconnect: [connectionDataHandler],
    };
}

export function getDataSetByKey(key: keyof TrackingDataSet): any {
    const handler = dataHandlers.find(dataHandler => Object.prototype.hasOwnProperty.call(dataHandler.data, key));

    if (handler !== undefined && handler.data) return handler.data;
}

export function track<T extends keyof TrackingDataSet>(
    key: T,
    value: TrackingDataSet[T],
    method: TrackMethod = TrackMethod.SET
) {
    // try {
    //     const dataSet: any = getDataSetByKey(key);

    //     if (method === TrackMethod.SET || dataSet[key] === undefined) {
    //         dataSet[key] = value;
    //         return;
    //     }
    //     if (method === TrackMethod.ONCE) {
    //         if (!dataSet[key]) {
    //             dataSet[key] = value;
    //         }
    //     }

    //     if (typeof dataSet[key] === 'number' && typeof value === 'number') {
    //         switch (method) {
    //             case TrackMethod.ADD:
    //                 dataSet[key] += value;
    //                 break;
    //             case TrackMethod.SUBTRACT:
    //                 dataSet[key] -= value;
    //                 break;
    //             case TrackMethod.MAX:
    //                 dataSet[key] = Math.max(value, dataSet[key] as number);
    //                 break;
    //             case TrackMethod.MIN:
    //                 dataSet[key] = Math.min(value, dataSet[key] as number);
    //                 break;
    //             case TrackMethod.STREAK:
    //                 if (!streakValues[key]) streakValues[key] = 0;
    //                 streakValues[key] += value;
    //                 //track(key, streakValues[key], TrackMethod.MAX);
    //                 break;
    //             case TrackMethod.STREAK_END:
    //                 streakValues[key] = 0;
    //                 break;
    //         }
    //     }
    // } catch (error) {
    //     report('Tracking exception', { error, once: true, sampleRate: 0.1 });
    // }
}
const streakValues: { [key in keyof TrackingDataSet]: number } = {};

export function queueQuestCompleteEvent(questData: IPlayerStateQuestData): Promise<void> {
    return new Promise((resolve: Function, reject: Function) => {
        requestAnimationFrame(() => {
            QuestHelper.trackCompletedQuest(questData);
            resolve();
        });
    });
}

// SOFA-9824:
// TODO: Remove this function once the clearing of data for events is reorganized,
// as it stands, it is nestled in the getEventData function of each EventDataHandler.
export function resetEventHandlerData<T>(eventName: allTrackingEvents, state: IGameState) {
    // try {
    //     syncGameStateWithTracking(state);
    //     const event: any = {
    //         ...eventDataHandler.getEventData(eventName),
    //         eventParams: {
    //             ...metaDataHandler.getEventData(eventName as TrackingEventName),
    //         },
    //     };
    //     trackingEventDataMap[eventName].forEach(
    //         eventDataHandler =>
    //             (event.eventParams = {
    //                 ...event.eventParams,
    //                 ...eventDataHandler.getEventData(eventName),
    //             })
    //     );
    //     // fixme: proper per-platform tracking for params
    //     if ([Platform.ANDROID, Platform.IOS].includes(Game.config.platform))
    //         event.eventParams.sessionEventCounter = sessionEventCounter++;
    // } catch (e) {
    //     console.info('[Tracking] Failed to reset handler data:', e);
    // }
}

export function syncGameStateWithTracking(state: IGameState) {
    track(
        'userID',
        Game.sdk.getUserId() || state.playerState.facebookUserId || state.playerState.deviceId || STRING_DATA_NOT_FOUND
    );
    track('loadingTime', trackLoadingTime(), TrackMethod.ONCE);

    // INVENTORY
    track('coins', state.playerState.inventory.coins);
    track('massRemoveBooster', state.playerState.inventory[BoosterType.TNT]);
    track('freeEntryBooster', state.playerState.inventory[RewardType.FREE_PLAY]);
    track('plus5Booster', state.playerState.inventory[BoosterType.PLUS_FIVE]);
    track('removeOneBooster', state.playerState.inventory[BoosterType.BULLS_EYE]);
    track('changerBooster', state.playerState.inventory[BoosterType.SHUFFLE]);
    track('jokerBooster', state.playerState.inventory[BoosterType.JOKER]);
    track('undoBooster', state.playerState.inventory[BoosterType.UNDO]);
    track('removeCardsBoosters', state.playerState.inventory[PreBoosterType.REMOVE_THREE]);
    track('extraWildcardsBoosters', state.playerState.inventory[PreBoosterType.JOKER]);
    track('canisterCardBoosters', state.playerState.inventory[PreBoosterType.CANISTER]);

    // MY FARM
    track('plot1Unlocked', state.playerState.farmState.unlockedAreas.includes('area_1') ? 1 : 0);
    track('plot2Unlocked', state.playerState.farmState.unlockedAreas.includes('area_2') ? 1 : 0);
    track('plot3Unlocked', state.playerState.farmState.unlockedAreas.includes('area_3') ? 1 : 0);
    track('plot4Unlocked', state.playerState.farmState.unlockedAreas.includes('area_4') ? 1 : 0);

    // PROGRESSION
    const stars: number = getLevelStarsTotal(state.playerState);
    track('stars', stars == null ? 0 : stars);
    const starCount =
        state.playerState.starChestLevel === null
            ? 0
            : ProgressStateHelper.getStarsCount(state.playerState, LevelType.Core) -
              state.playerState.progress.starChestInitialStars;
    track('starsInNextChest', starCount);

    track('highestLevel', getCurrentLevelNumber(state.playerState));

    // SETTINGS
    track('musicIsOn', Number(state.playerState.settings.audioMusic));
    track('soundIsOn', Number(state.playerState.settings.audioSfx));

    // MISC
    track('timezoneName', getTimeZone());

    track('realMoneyPurchasesMade', state.playerState.purchases);
    track('realMoneySpent', state.playerState.realMoneySpent);
    track('coinValueBoughtWithRealMoney', state.playerState.coinValueBoughtWithRealMoney);

    track('usedCheatCodes', state.playerState.usedCheats ? 1 : 0);

    track('attributionCampaignID', state.playerState.attributionCampaignID);

    // QUESTS
    for (let i = 1; i <= 3; i++) {
        let quest: IPlayerStateQuestData = state.playerState.quests[i - 1];
        if (!quest || !quest.id) {
            quest = {
                id: undefined,
                type: undefined,
                tier: undefined,
                progress: undefined,
            } as IPlayerStateQuestData;
        }
        track(`quest${i.toString()}Name` as keyof TrackingDataSet, quest.type);
        track(`quest${i.toString()}ID` as keyof TrackingDataSet, quest.id);
        track(`quest${i.toString()}Tier` as keyof TrackingDataSet, quest.tier);
        track(`quest${i.toString()}Progress` as keyof TrackingDataSet, quest.progress);
    }

    track('mIsHardLevel', Number(WorldDataHelper.isHardLevel(state.levelState.levelNumber)));
}

export function trackLoadingTime() {
    if (typeof window !== 'undefined') {
        return Date.now() - (window as any).JS_INIT_TIME;
    }
    return Date.now();
}

export function clearTrackingData<T extends IDataHandler<TransactionDataSet>>(ctor: { new (): T }) {
    const handler: IDataHandler<IDataSet> = dataHandlers.find(
        (handler: IDataHandler<IDataSet>) => handler instanceof ctor
    );

    if (handler === undefined) return;

    handler.clearData();
}

export function getTrackingDataSet(): TrackingDataSet {
    return {
        ...eventDataHandler.data,
        ...metaDataHandler.data,
        ...missionDataHandler.data,
        ...entryPointDataHandler.data,
        ...adDataHandler.data,
        ...resourceDataHandler.data,
        ...transactionDataHandler.data,
        ...socialActionHandler.data,
        ...ftuxDataHandler.data,
        ...questDataHandler.data,
        ...userDataHandler.data,
        ...dataRestoreHandler.data,
        ...connectionDataHandler.data,
    };
}